import { useState } from "react";
import { Copy, Rocket, Repeat2, Clock, Ban, ShoppingBag, Share2 } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDateLong } from "../../../utils/function";
import { LinkItemMenu } from "./dropdown";
import { DiscoveryActivityItem } from "./types";
import { ShareModal } from "../../../components/waitlish-dashboard/media-popup";

const StatusPill: React.FC<{ isActive: boolean }> = ({ isActive }) =>
  isActive ? (
    <span className="flex items-center gap-1 text-[#333D4D] text-xs font-semibold">
      <Clock size={13} className="text-[#00A86B]" /> Active Link
    </span>
  ) : (
    <span className="flex items-center gap-1 text-gray-400 text-xs font-semibold">
      <Ban size={13} /> Disabled
    </span>
  );

interface LinkActivityItemProps {
  item: DiscoveryActivityItem;
  onToggleActive: () => void;
}

export const LinkActivityItem: React.FC<LinkActivityItemProps> = ({ item, onToggleActive }) => {
  const [isShareOpen, setIsShareOpen] = useState(false);

  const handleCopy = () => {
    navigator.clipboard?.writeText(item.click_url);
    toast.success("Link copied to clipboard");
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-5 border-b border-gray-50 last:border-b-0">
      {/* Left: thumbnail + details */}
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 bg-slate-100 overflow-hidden">
          {item.image_url ? (
            <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
          ) : (
            <ShoppingBag size={22} className="text-slate-400" />
          )}
        </div>

        <div className="min-w-0">
          <p className="font-bold text-black text-sm">{item.domain}</p>
          <p className="text-sm text-gray-500 truncate max-w-xs">{item.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">by {item.recommender_name}</p>

          <div className="flex items-center flex-wrap gap-4 mt-2">
            {!item.is_active && <StatusPill isActive={false} />}
            <span className="flex items-center gap-1 text-[#333D4D] text-xs font-semibold">
              <Rocket size={13} className="text-[#00A86B]" /> Discovery Rewards
            </span>
            <span className="flex items-center gap-1 text-[#333D4D] text-xs font-semibold">
              <Repeat2 size={13} className="text-[#00A86B]" /> Earnings on Clicks
            </span>
            {item.is_active && <StatusPill isActive={true} />}
          </div>

          <p className="text-xs text-gray-400 mt-2">Created {formatDateLong(new Date(item.created_at))}</p>
        </div>
      </div>

      {/* Right: clicks/earnings + actions */}
      <div className="flex items-center justify-between sm:justify-end gap-8 flex-shrink-0 pl-[72px] sm:pl-0">
        <div className="flex gap-8 text-right">
          <div>
            <p className="text-xs text-gray-400 mb-1">Clicks</p>
            <p className="text-sm font-bold text-[#00A86B]">{item.unique_click_count}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Earnings</p>
            <p className="text-sm font-bold text-[#00A86B]">{formatCurrency(item.earnings, "NGN", "symbol")}</p>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button onClick={handleCopy} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-50" aria-label="Copy link">
            <Copy size={16} />
          </button>
          <button onClick={() => setIsShareOpen(true)} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-50" aria-label="Share link">
            <Share2 size={16} />
          </button>
          <LinkItemMenu isActive={item.is_active} onToggleActive={onToggleActive} />
        </div>
      </div>

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        shareLink={item.click_url}
        title="Share this Discovery Link"
        description="Help this recommendation reach more people."
      />
    </div>
  );
};
